import { useState, useCallback } from 'react';
import { BeneficiariesFieldsProps, DocumentsProps } from '../features/customers/customer.types';

interface CustomerFormData {
  id?: number;
  email: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  rfc: string;
  curp: string;
  gender: string;
  phone: string;
  address: string;
  zipCode: string;
  state: string;
  city: string;
  addressNumber: string;
  neighborhood: string;
  bankId: number;
  clabe: string;
  documents: DocumentsProps[];
  beneficiaries: BeneficiariesFieldsProps[];
}

interface UseCustomerFormReturn {
  formData: CustomerFormData;
  updateField: (field: string, value: any) => void;
  updateBeneficiary: (index: number, field: string, value: any) => void;
  addBeneficiary: () => void;
  removeBeneficiary: (index: number) => void;
  addDocument: (document: DocumentsProps) => void;
  addBeneficiaryDocument: (beneficiaryIndex: number, document: DocumentsProps) => void;
  resetForm: () => void;
  setFormData: (data: Partial<CustomerFormData>) => void;
  validateForm: () => { isValid: boolean; errors: string[] };
}

const initialFormData: CustomerFormData = {
  email: '',
  firstName: '',
  lastName: '',
  birthDate: '',
  rfc: '',
  curp: '',
  gender: '',
  phone: '',
  address: '',
  zipCode: '',
  state: '',
  city: '',
  addressNumber: '',
  neighborhood: '',
  bankId: 1,
  clabe: '',
  documents: [],
  beneficiaries: [],
};

const initialBeneficiary: BeneficiariesFieldsProps = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  rfc: '',
  curp: '',
  birthDate: '',
  gender: '',
  state: '',
  city: '',
  address: '',
  zipCode: '',
  addressNumber: '',
  neighborhood: '',
  bankId: 1,
  clabe: '',
  documents: [],
  isNew: true,
};

export const useCustomerForm = (initialData?: Partial<CustomerFormData>): UseCustomerFormReturn => {
  const [formData, setFormDataState] = useState<CustomerFormData>({
    ...initialFormData,
    ...initialData,
  });

  const updateField = useCallback((field: string, value: any) => {
    setFormDataState(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const updateBeneficiary = useCallback((index: number, field: string, value: any) => {
    setFormDataState(prev => ({
      ...prev,
      beneficiaries: prev.beneficiaries.map((beneficiary, i) =>
        i === index ? { ...beneficiary, [field]: value } : beneficiary
      ),
    }));
  }, []);

  const addBeneficiary = useCallback(() => {
    setFormDataState(prev => ({
      ...prev,
      beneficiaries: [...prev.beneficiaries, { ...initialBeneficiary }],
    }));
  }, []);

  const removeBeneficiary = useCallback((index: number) => {
    setFormDataState(prev => ({
      ...prev,
      beneficiaries: prev.beneficiaries.filter((_, i) => i !== index),
    }));
  }, []);

  const addDocument = useCallback((document: DocumentsProps) => {
    setFormDataState(prev => ({
      ...prev,
      documents: [
        ...prev.documents.filter(doc => doc.documentTypeId !== document.documentTypeId),
        document,
      ],
    }));
  }, []);

  const addBeneficiaryDocument = useCallback((beneficiaryIndex: number, document: DocumentsProps) => {
    setFormDataState(prev => ({
      ...prev,
      beneficiaries: prev.beneficiaries.map((beneficiary, i) =>
        i === beneficiaryIndex
          ? {
              ...beneficiary,
              documents: [
                ...(beneficiary.documents || []).filter(doc => doc.documentTypeId !== document.documentTypeId),
                document,
              ],
            }
          : beneficiary
      ),
    }));
  }, []);

  const resetForm = useCallback(() => {
    setFormDataState({ ...initialFormData });
  }, []);

  const setFormData = useCallback((data: Partial<CustomerFormData>) => {
    setFormDataState(prev => ({ ...prev, ...data }));
  }, []);

  const validateForm = useCallback((): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Required fields validation
    const requiredFields = [
      { field: 'email', label: 'Email' },
      { field: 'firstName', label: 'Nombre(s)' },
      { field: 'lastName', label: 'Apellidos' },
      { field: 'rfc', label: 'RFC' },
      { field: 'phone', label: 'Teléfono' },
      { field: 'address', label: 'Dirección' },
      { field: 'zipCode', label: 'Código postal' },
      { field: 'state', label: 'Estado' },
      { field: 'city', label: 'Ciudad' },
      { field: 'addressNumber', label: 'Número exterior' },
      { field: 'neighborhood', label: 'Colonia' },
      { field: 'clabe', label: 'CLABE' },
    ];

    requiredFields.forEach(({ field, label }) => {
      const value = (formData as any)[field];
      if (!value || value.toString().trim() === '') {
        errors.push(`${label} es requerido`);
      }
    });

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.push('Email no tiene un formato válido');
    }

    // RFC validation
    if (formData.rfc && !/^[A-ZÑ&]{3,4}[0-9]{6}[A-V1-9][A-Z1-9][0-9A]$/.test(formData.rfc.toUpperCase())) {
      errors.push('RFC no tiene un formato válido');
    }

    // CLABE validation
    if (formData.clabe && formData.clabe.length !== 18) {
      errors.push('CLABE debe tener exactamente 18 dígitos');
    }

    // Phone validation
    if (formData.phone && !/^[0-9]{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      errors.push('Teléfono debe tener 10 dígitos');
    }

    // ZIP code validation
    if (formData.zipCode && !/^[0-9]{5}$/.test(formData.zipCode)) {
      errors.push('Código postal debe tener 5 dígitos');
    }

    // Documents validation (3 required documents)
    if (formData.documents.length !== 3) {
      errors.push('Se requieren exactamente 3 documentos: INE, comprobante de domicilio y constancia fiscal');
    }

    // Validate each beneficiary
    formData.beneficiaries.forEach((beneficiary, index) => {
      if (!beneficiary.firstName?.trim()) {
        errors.push(`Beneficiario ${index + 1}: Nombre es requerido`);
      }
      if (!beneficiary.lastName?.trim()) {
        errors.push(`Beneficiario ${index + 1}: Apellidos son requeridos`);
      }
      if (!beneficiary.rfc?.trim()) {
        errors.push(`Beneficiario ${index + 1}: RFC es requerido`);
      } else if (!/^[A-ZÑ&]{3,4}[0-9]{6}[A-V1-9][A-Z1-9][0-9A]$/.test(beneficiary.rfc.toUpperCase())) {
        errors.push(`Beneficiario ${index + 1}: RFC no tiene un formato válido`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  }, [formData]);

  return {
    formData,
    updateField,
    updateBeneficiary,
    addBeneficiary,
    removeBeneficiary,
    addDocument,
    addBeneficiaryDocument,
    resetForm,
    setFormData,
    validateForm,
  };
};
